// src/utils/macroCalculator.js

const MIN_CALORIES = 1200;

/**
 * Calculate macro targets given a calorie goal, weight, and protein preference.
 *
 * Priority order:
 *  1. Protein (g) = weight_lbs × protein_per_lb
 *  2. Remaining calories split 40/30 carb/fat (normalised)
 *
 * @param {object} params
 * @param {number} params.calorie_target   - Total daily calories
 * @param {number} params.weight_lbs       - Current body weight in lbs
 * @param {number} params.protein_per_lb   - 0.7–1.0
 * @param {number} [params.carb_pct]       - Share of remaining cals for carbs (default 0.571 ≈ 40/70)
 * @param {number} [params.fat_pct]        - Share of remaining cals for fat  (default 0.429 ≈ 30/70)
 * @returns {{ protein_target, carb_target, fat_target, carb_pct, fat_pct }}
 */
export function calculateMacros({
  calorie_target,
  weight_lbs,
  protein_per_lb = 0.8,
  carb_pct = 0.571,
  fat_pct  = 0.429,
}) {
  const safeCalories = Math.max(calorie_target, MIN_CALORIES);

  const protein_target = Math.round(weight_lbs * protein_per_lb);
  const protein_cals   = protein_target * 4;
  const remaining_cals = Math.max(safeCalories - protein_cals, 0);

  const carb_target = Math.round((remaining_cals * carb_pct) / 4);
  const fat_target  = Math.round((remaining_cals * fat_pct)  / 9);

  return {
    protein_target,
    carb_target,
    fat_target,
    carb_pct,
    fat_pct,
  };
}

/**
 * Returns macros as percentage of total caloric intake.
 */
export function macroPercentages({ protein_g, carb_g, fat_g }) {
  const total = protein_g * 4 + carb_g * 4 + fat_g * 9;
  if (total === 0) return { protein: 0, carbs: 0, fat: 0 };
  return {
    protein: Math.round((protein_g * 4 / total) * 100),
    carbs:   Math.round((carb_g   * 4 / total) * 100),
    fat:     Math.round((fat_g    * 9 / total) * 100),
  };
}
